/**
 * Baron von Blubba (02 §6). A software sprite run once per main-loop pass (M-tick), one per player
 * (types $2E / $30). The code is at $1473, mislabelled `player_death_handler` in rebb64 (the
 * `baron_von_blubba` label at $10D3 is the red-cross shot); spawned at $1621 (mislabelled
 * `game_init_sequence`).
 *
 * It hunts its own player in L-shaped legs, horizontal first, 1, 2, 3 ... 15 cells long, each after
 * a 30-pass pause, one cell (8 px) a pass through anything, a leg ending early once it lines up
 * with the player's column or row. It kills whichever player it touches, even while pausing, and
 * then leaves; it also leaves when its own player is dying.
 */

import { initialBaronAi } from './state';
import { BARON } from './tuning';
import { killPlayer } from '../players';
import { rngOf } from '../random';
import type { Baron, PlayerId, World } from '../types';

const HORIZONTAL = 0x80;

/** Keep the sprite-frame position in step with the software cell (see tuning `BARON.baseX`). */
function place(b: Baron): void {
  b.x = b.ai.col * 8 + BARON.baseX;
  b.y = b.ai.row * 8 + BARON.baseY;
}

/**
 * $1621: one corner roll per player slot (player 2's first, as the loop runs X = 1, 0), then a
 * Baron for each player in play.
 */
export function spawnBaronsNow(world: World): void {
  const rng = rngOf(world);
  const corner: Array<[number, number]> = [];
  for (const id of [1, 0] as const) corner[id] = [BARON.cols[rng.int(2)] ?? 2, BARON.rows[rng.int(2)] ?? 5];
  for (const id of [0, 1] as const) {
    const [col, row] = corner[id] ?? [2, 5];
    if (world.players[id].state !== 'play') continue;
    const b: Baron = { id: world.nextId++, x: 0, y: 0, target: id, ai: initialBaronAi(col, row, world.clock.mTicks) };
    place(b);
    world.barons.push(b);
  }
}

/** L_152B with its bounds bumped ($1496): a player in normal play, not respawning, within the box. */
function touched(world: World, b: Baron): PlayerId | -1 {
  const cx = b.ai.col * 8 + BARON.hitX;
  const cy = b.ai.row * 8 + BARON.baseY;
  for (const id of [1, 0] as const) {
    const p = world.players[id];
    if (p.state !== 'play' || p.invulnerable > 0) continue;
    if (Math.abs(p.x - cx) < BARON.reach && Math.abs(p.y - cy) < BARON.reach) return id;
  }
  return -1;
}

/** One cell toward `to` along one axis; false when already lined up (the leg ends, L_1523). */
function stepToward(from: number, to: number): number | null {
  if (from === to) return null;
  return from < to ? from + 1 : from - 1;
}

/** One pass of one Baron ($1473). Returns false when it leaves. */
function baronPass(world: World, b: Baron): boolean {
  const a = b.ai;
  const own = world.players[b.target];
  if (own.state === 'dying') return false;
  const victim = touched(world, b);
  if (victim !== -1) {
    killPlayer(world, victim);
    return false;
  }
  if ((a.steps & 0x7f) === 0) {
    // A new leg: one longer (to 15), on the other axis, after a pause.
    a.leg = (a.leg + 1) & 0xff;
    if ((a.leg & 0x0f) === 0) a.leg = (a.leg - 1) & 0xff;
    a.leg ^= HORIZONTAL;
    a.steps = a.leg;
    a.pause = BARON.pause;
  }
  if (a.pause > 0) {
    a.pause--;
    return true;
  }
  const col = ((own.x - 0x14) & 0xff) >> 3;
  const row = ((own.y - 0x15) & 0xff) >> 3;
  a.faceLeft = col < a.col;
  a.steps = (a.steps - 1) & 0xff;
  const horizontal = (a.steps & HORIZONTAL) !== 0;
  const next = horizontal ? stepToward(a.col, col) : stepToward(a.row, row);
  if (next === null) a.steps = 0;
  else if (horizontal) a.col = next;
  else a.row = next;
  place(b);
  return true;
}

/**
 * Run every main-loop pass since the Barons last ran. The world calls the monsters on I-ticks only,
 * so passes are caught up from `world.clock.mTicks` (at most a frame late); calling this on the
 * M-tick itself as well is harmless - each pass runs once. Nothing moves while play is frozen, nor
 * under the clock: the software-sprite loop skips types $24..$33 then (L0D4E, $0D55), so the Baron
 * stands and kills nobody.
 */
export function updateBarons(world: World): void {
  const now = world.clock.mTicks;
  const frozen = world.flow.freeze > 0 || world.power.clock > 0;
  world.barons = world.barons.filter((b) => {
    let keep = true;
    while (keep && b.ai.mTick < now) {
      b.ai.mTick++;
      if (!frozen) keep = baronPass(world, b);
    }
    return keep;
  });
}
